import { useMemo } from 'react';

import useChartOptions from '@/components/Chart/hooks/options/useChartOptions';
import useSpotOptions from '@/components/Chart/hooks/options/useSpotOptions';
import useChartSeries from '@/components/Chart/hooks/useChartSeries';
import type { PreviewChartProps } from '@/features/DataHub/constants/charts';
import { DATAHUB_DEFAULT_GROUP } from '@/features/DataHub/constants/const';
import { useExportSettings } from '@/features/DataHub/modules/Export/context/ExportSettingsContext';
import { getXAxisRangeStrings } from '@/features/DataHub/utils/getXAxisRangeStrings';
import type { TemplateChartSeries } from '@/pages/Markets/MarketsComponents/types';

import PreviewChartLayout from './PreviewChartLayout';
import usePreviewOptions from './usePreviewOptions';

const PreviewSpotChart = ({ type, symbols }: PreviewChartProps) => {
  const { exportSettings } = useExportSettings();
  const templateChartSeries: TemplateChartSeries[] = useMemo(() => {
    return Array.from(symbols)
      .slice(0, 6)
      .map(symbol => ({
        settings: { type: 'line' as const },
        symbol: {
          group: DATAHUB_DEFAULT_GROUP,
          key: {
            Code: symbol.Code,
            PeriodType: symbol.PeriodType,
            Period: symbol.Period,
            TimeRef: symbol.TimeRef
          }
        }
      }));
  }, [symbols]);

  const { loading, error, chartSeries } = useChartSeries(templateChartSeries, true);

  const { createOptions } = useSpotOptions();
  const { options: baseOptions } = useChartOptions(chartSeries, createOptions);
  const previewOptions = usePreviewOptions(baseOptions);
  const { xAxisMin, xAxisMax } = getXAxisRangeStrings(exportSettings);

  const options = useMemo(() => {
    if (!previewOptions) return undefined;

    return {
      ...previewOptions,
      xAxis: {
        ...previewOptions.xAxis,
        min: xAxisMin,
        max: xAxisMax
      }
    };
  }, [previewOptions, xAxisMin, xAxisMax]);

  return (
    <PreviewChartLayout
      chartSeries={chartSeries}
      chartType={type}
      error={error}
      loading={loading}
      options={options}
    />
  );
};

export default PreviewSpotChart;
